import { useEffect, useRef } from "react";

/**
 * Registers the PWA service worker and FORCES auto-update: the worker calls
 * skipWaiting() on install and claims clients on activate, so when a new version
 * is deployed the page reloads itself (no manual "Refresh" tap). Polls for new
 * deploys every 30s on long-lived sessions.
 *
 * Renders nothing — it's a side-effect-only mount point in <App/>.
 */
export default function PWAUpdatePrompt() {
  const reloadingRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

    let registration = null;

    const register = () =>
      navigator.serviceWorker
        .register("/sw.js")
        .then((reg) => {
          registration = reg;
          reg.update?.().catch(() => {});
        })
        .catch(() => {});

    if (document.readyState === "complete") {
      register();
    } else {
      window.addEventListener("load", register, { once: true });
    }

    // When a new worker takes control (after skipWaiting), reload once to swap
    // in the fresh bundle automatically.
    const onControllerChange = () => {
      if (reloadingRef.current) return;
      reloadingRef.current = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    // Poll for new deploys on long-lived / resident PWA sessions.
    const interval = setInterval(() => {
      registration?.update?.().catch(() => {});
    }, 30 * 1000);

    return () => {
      clearInterval(interval);
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  return null;
}
